import { useInfiniteQuery } from "@tanstack/react-query";
import { useState } from "react";

import {
  listUnitAlerts,
  type UnitAlertsResponse,
} from "@/api/fleet";
import { formatDate } from "@/lib/utils";

interface Props {
  unitId: number;
}

export function AlertsTab({ unitId }: Props) {
  const [openOnly, setOpenOnly] = useState(false);
  const [severity, setSeverity] = useState<string>("");

  const q = useInfiniteQuery<UnitAlertsResponse>({
    queryKey: ["unit-alerts", unitId, { openOnly, severity }],
    initialPageParam: undefined as number | undefined,
    queryFn: ({ pageParam }) =>
      listUnitAlerts(unitId, {
        openOnly,
        severity: severity || undefined,
        limit: 50,
        cursor: pageParam as number | undefined,
      }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const rows = q.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <section className="space-y-3" data-testid="unit-alerts">
      <header className="flex flex-wrap items-baseline gap-3">
        <h2 className="text-sm font-semibold">Alertas</h2>
        <label className="text-xs flex items-center gap-1">
          <input
            type="checkbox"
            checked={openOnly}
            onChange={(e) => setOpenOnly(e.target.checked)}
          />
          Sólo abiertas
        </label>
        <select
          className="select text-xs"
          value={severity}
          onChange={(e) => setSeverity(e.target.value)}
        >
          <option value="">Todas severidades</option>
          <option value="info">info</option>
          <option value="warning">warning</option>
          <option value="critical">critical</option>
        </select>
        <p className="ml-auto text-xs text-muted-200">
          {rows.length} resultado(s)
        </p>
      </header>

      {q.isLoading && <div className="text-muted-200">Cargando…</div>}

      <div className="space-y-2">
        {rows.map((a) => (
          <article
            key={a.id}
            className={
              "rounded-md border p-2 text-sm " +
              (a.severidad === "critical"
                ? "border-rose-500/50 bg-rose-500/10"
                : a.severidad === "warning"
                  ? "border-amber-500/50 bg-amber-500/10"
                  : "border-border bg-surface2/30")
            }
          >
            <header className="flex items-center justify-between text-xs">
              <span className="font-medium uppercase">{a.tipo}</span>
              <span className="text-muted-200">{a.severidad}</span>
            </header>
            <div className="text-xs text-muted-200">
              Inicio: {formatDate(a.iniciadoAt)}
              {a.resueltoAt && (
                <>
                  {" "}
                  · Resuelta: {formatDate(a.resueltoAt)}
                </>
              )}
            </div>
            {a.payload && Object.keys(a.payload).length > 0 && (
              <pre className="mt-1 text-[11px] text-muted-200 whitespace-pre-wrap">
                {JSON.stringify(a.payload, null, 2)}
              </pre>
            )}
            {a.comentarioResolucion && (
              <p className="mt-1 text-xs text-emerald-300">
                {a.comentarioResolucion}
              </p>
            )}
          </article>
        ))}
        {rows.length === 0 && !q.isLoading && (
          <p className="text-center text-muted-200 text-sm py-4">
            Sin alertas para los filtros actuales.
          </p>
        )}
      </div>

      {q.hasNextPage && (
        <div className="text-center">
          <button
            className="btn-ghost text-xs"
            onClick={() => q.fetchNextPage()}
            disabled={q.isFetchingNextPage}
          >
            {q.isFetchingNextPage ? "Cargando…" : "Cargar más"}
          </button>
        </div>
      )}
    </section>
  );
}
